import {
  Component,
  DestroyRef,
  ElementRef,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { catchError, finalize, of } from 'rxjs';

import { SAVING_ARENA_API_CONFIG } from '../../../core/api/api.config';
import {
  FrontendBlog,
  FrontendCategoriesPage,
  FrontendCouponCategory,
  FrontendReview,
  FrontendReviewsSummary,
} from '../../../core/api/api.models';
import { SavingArenaApiService } from '../../../core/api/saving-arena-api.service';
import { TrustedCmsHtmlPipe } from '../../../core/api/trusted-cms-html.pipe';
import { SeoService } from '../../../core/seo/seo.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../core/seo/breadcrumb-trail';
import { webPageSchema } from '../../../core/seo/structured-data';
import { Breadcrumb } from '../../../shared/breadcrumb/breadcrumb';
import { LatestInsights } from '../../../shared/latest-insights/latest-insights';
import { LongformContent } from '../../../shared/longform-content/longform-content';

type CategoryIcon = 'apparel' | 'ai' | 'electronics' | 'entertainment' | 'business';
type SubcategoryIcon = 'shirt' | 'spark' | 'device' | 'briefcase';

interface FeaturedCategory {
  icon: CategoryIcon;
  iconHtml: SafeHtml | null;
  iconImageUrl: string;
  slug: string;
  coupons: number;
  stores: number;
  title: string;
}

interface CategoryGroup {
  icon: CategoryIcon;
  iconHtml: SafeHtml | null;
  iconImageUrl: string;
  slug: string;
  title: string;
  items: {
    label: string;
    slug: string;
    icon: SubcategoryIcon;
    iconHtml: SafeHtml | null;
    iconImageUrl: string;
  }[];
}

interface ApiCategoryParent {
  id: string;
  name: string;
  slug?: string;
}

interface ApiCouponCategory extends FrontendCouponCategory {
  isChild?: boolean;
  isFeatured?: boolean;
  parentCategory?: ApiCategoryParent | null;
  iconType?: string;
  iconImagePath?: string;
  iconCode?: string;
  totalCoupons?: number;
  totalStores?: number;
}

interface FaqItem {
  question: string;
  answer: string;
}

interface ReviewVm {
  id: string;
  title: string;
  description: string;
  initials: string;
  userName: string;
  roleLabel: string;
  dateLabel: string;
  stars: number[];
}

@Component({
  selector: 'app-categories-page',
  templateUrl: './categories-page.html',
  styleUrls: ['./categories-page.scss'],
  imports: [Breadcrumb, LatestInsights, TrustedCmsHtmlPipe, LongformContent],
})
export class CategoriesPage {
  /** Rendered by `<app-breadcrumb>` and turned into the page's BreadcrumbList — one definition. */
  protected readonly crumbs = breadcrumbTrail({ label: 'Categories' });

  private readonly api = inject(SavingArenaApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly apiConfig = inject(SAVING_ARENA_API_CONFIG);
  private readonly seo = inject(SeoService);

  // Admin-managed content (Settings › Page Settings › Categories Page). Empty until saved once,
  // in which case the built-in defaults below remain in place.
  readonly pageH1 = signal('');
  readonly pageShortDescription = signal('');
  readonly adminFaqs = signal<FaqItem[]>([]);
  readonly longDescriptionHtml = signal('');
  readonly featuredBlogs = signal<FrontendBlog[]>([]);
  readonly hasFeaturedBlogs = computed(() => this.featuredBlogs().length > 0);

  // FAQ list actually rendered: admin FAQs when configured, otherwise the built-in defaults.
  readonly displayedFaqs = computed(() =>
    this.adminFaqs().length > 0 ? this.adminFaqs() : this.faqItems,
  );

  @ViewChild('featuredCarousel') private readonly featuredCarousel?: ElementRef<HTMLElement>;
  readonly featuredCategories = signal<FeaturedCategory[]>([]);
  readonly shouldShowFeaturedControls = computed(() => this.featuredCategories().length > 4);
  readonly categoryGroups = signal<CategoryGroup[]>([]);
  readonly isLoadingCategories = signal(true);
  readonly hasCategoryError = signal(false);

  // Latest approved shopper reviews (from the /reviews endpoint) for the "What shoppers say" section.
  readonly reviews = signal<ReviewVm[]>([]);
  readonly reviewsRating = signal(0);
  readonly reviewsCount = signal(0);
  readonly hasReviews = computed(() => this.reviews().length > 0);

  readonly faqItems: FaqItem[] = [
    {
      question: 'How do I use a coupon code on SavingArena?',
      answer:
        "Browse our catalog of coupons and deals, click on the one you want to use, and copy the code. Then shop at the retailer's website and paste the code at checkout to apply your savings.",
    },
    {
      question: 'Are the coupons on SavingArena really free?',
      answer:
        'Yes! All coupons and deals on SavingArena are completely free to use. We partner with retailers to bring you exclusive discounts without any hidden fees or subscription requirements.',
    },
    {
      question: 'How often are the deals and coupons updated?',
      answer:
        'Our team refreshes deals daily to ensure you have access to the latest discounts. We remove expired codes automatically and add new ones as soon as they become available from our retail partners.',
    },
    {
      question: 'Do I need to create an account to use SavingArena?',
      answer:
        'No account is required. You can browse and copy coupon codes immediately. However, creating a free account gives you access to personalized deal alerts, price drop notifications, and a saved wishlist.',
    },
    {
      question: 'How does SavingArena make money?',
      answer:
        "We earn a small commission from retailers when you make a purchase using our links or codes. This doesn't affect the savings you get - in fact, it allows us to keep the service free for everyone.",
    },
    {
      question: "What should I do if a coupon code doesn't work?",
      answer:
        "If a code doesn't work, first double-check that you've entered it correctly. Some codes have minimum purchase requirements or exclusions. If it still fails, please report it so we can verify or replace it promptly.",
    },
  ];

  readonly expandedFaqIndex = signal<number | null>(null);

  constructor() {
    this.api
      .getCouponCategories()
      .pipe(
        catchError(() => {
          this.hasCategoryError.set(true);
          return of({ success: false, data: [] });
        }),
        finalize(() => this.isLoadingCategories.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.featuredCategories.set(this.toFeaturedCategories(response.data));
        this.categoryGroups.set(this.toCategoryGroups(response.data));
      });

    this.api
      .getCategoriesPageSettings()
      .pipe(
        catchError(() => of({ success: false, data: null })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.applyPageSettings(response.data);
        // Applied here (not in the constructor) so the admin's meta title/description are in the
        // DOM before SSR serialises it; it still runs when the settings are missing or fail to
        // load, falling back to the generated copy.
        this.applyCategoriesSeo(response.data);
      });

    this.api
      .getLatestReviews(4)
      .pipe(
        catchError(() => of({ success: false, data: null })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => this.applyReviews(response.data));
  }

  private applyReviews(summary: FrontendReviewsSummary | null | undefined): void {
    if (!summary || !Array.isArray(summary.reviews)) {
      return;
    }

    const reviews = summary.reviews
      .map((review) => this.toReviewVm(review))
      .filter((review) => review.title || review.description);
    this.reviews.set(reviews);
    this.reviewsRating.set(Number(summary.rating) || 0);
    this.reviewsCount.set(Number(summary.ratingCount) || 0);
  }

  private toReviewVm(review: FrontendReview): ReviewVm {
    const userName = (review.userName ?? '').trim();
    const designation = (review.userDesignation ?? '').trim();
    const storeName = (review.storeName ?? '').trim();
    const rating = Math.max(1, Math.min(5, Math.round(Number(review.rating) || 5)));

    return {
      id: review.id,
      title: (review.title ?? '').trim(),
      description: (review.description ?? '').trim(),
      initials: this.toInitials(userName),
      userName: userName || 'Shopper',
      roleLabel: designation || (storeName ? `on ${storeName}` : ''),
      dateLabel: this.formatReviewDate(review.date),
      stars: Array.from({ length: rating }, (_, index) => index + 1),
    };
  }

  private toInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
      return 'SA';
    }
    const first = parts[0].charAt(0);
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
    return (first + last).toUpperCase();
  }

  private formatReviewDate(iso: string | null): string {
    if (!iso) {
      return '';
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  private applyPageSettings(settings: FrontendCategoriesPage | null | undefined): void {
    if (!settings) {
      return;
    }

    this.pageH1.set((settings.h1 ?? '').trim());
    this.pageShortDescription.set((settings.shortDescription ?? '').trim());
    this.longDescriptionHtml.set((settings.longDescription ?? '').trim());

    const faqs = Array.isArray(settings.faqs)
      ? settings.faqs
          .map((faq) => ({ question: (faq.question ?? '').trim(), answer: (faq.answer ?? '').trim() }))
          .filter((faq) => faq.question && faq.answer)
      : [];
    this.adminFaqs.set(faqs);

    this.featuredBlogs.set(Array.isArray(settings.blogs) ? settings.blogs : []);
  }

  /**
   * SEO for /categories. The admin's Meta Title / Meta Description remain the source of truth (they
   * were already applied through Title/Meta); SeoService adds the canonical, OG/Twitter tags and the
   * structured data that were missing.
   */
  private applyCategoriesSeo(settings: FrontendCategoriesPage | null | undefined): void {
    const path = '/categories';
    const url = this.seo.absolute(path);

    const title =
      (settings?.metaTitle ?? '').trim() ||
      'Shop by Category: Coupons, Promo Codes & Deals | SavingArena';
    const description =
      (settings?.metaDescription ?? '').trim() ||
      'Browse every SavingArena category and find verified coupon codes, promo codes and deals from the stores in each one.';

    this.seo.apply({ title, description, canonicalPath: path });
    this.seo.setJsonLd([
      webPageSchema({ origin: this.seo.origin, url, name: title, description }),
      breadcrumbSchemaFor(this.crumbs, (p) => this.seo.absolute(p)),
    ]);
  }

  // Hero short description: collapse to the first 30 words behind a "Read More" toggle.
  private readonly shortDescriptionWordLimit = 30;
  isShortDescriptionExpanded = false;

  get shortDescriptionText(): string {
    return (
      (this.pageShortDescription() || '').trim() ||
      'Discover our highest-rated categories, curated for premium quality and outstanding performance across industries.'
    );
  }

  get shouldShowShortDescriptionToggle(): boolean {
    return this.shortDescriptionText.split(/\s+/).filter(Boolean).length > this.shortDescriptionWordLimit;
  }

  get visibleShortDescription(): string {
    const text = this.shortDescriptionText;
    if (this.isShortDescriptionExpanded || !this.shouldShowShortDescriptionToggle) {
      return text;
    }

    return `${text.trim().split(/\s+/).filter(Boolean).slice(0, this.shortDescriptionWordLimit).join(' ')}...`;
  }

  toggleShortDescription(): void {
    this.isShortDescriptionExpanded = !this.isShortDescriptionExpanded;
  }

  toggleFaq(index: number): void {
    this.expandedFaqIndex.update((current) => (current === index ? null : index));
  }

  slideFeaturedCategories(direction: 'next' | 'prev'): void {
    const carouselElement = this.featuredCarousel?.nativeElement;

    if (!carouselElement) {
      return;
    }

    carouselElement.scrollBy({
      left: (direction === 'next' ? 1 : -1) * Math.max(carouselElement.clientWidth * 0.75, 280),
      behavior: 'smooth',
    });
  }

  showFallbackIcon(event: Event): void {
    (event.target as HTMLImageElement).classList.add('is-fallback-hidden');
  }

  private toCategoryGroups(categories: FrontendCouponCategory[]): CategoryGroup[] {
    const apiCategories = categories as ApiCouponCategory[];
    const childrenByParentId = new Map<string, ApiCouponCategory[]>();

    for (const category of apiCategories) {
      if (!category.isChild || !category.parentCategory) {
        continue;
      }

      const siblings = childrenByParentId.get(category.parentCategory.id) ?? [];
      siblings.push(category);
      childrenByParentId.set(category.parentCategory.id, siblings);
    }

    return apiCategories
      .filter((category) => !category.isChild)
      .map((category) => {
        const children = childrenByParentId.get(category.id) ?? [];

        return {
          icon: this.categoryIconFor(category),
          iconHtml: this.apiIconHtml(category),
          iconImageUrl: this.apiIconImageUrl(category),
          slug: this.categorySlug(category),
          title: category.name,
          items: children.map((item) => ({
            label: item.name,
            slug: this.categorySlug(item),
            icon: this.subcategoryIconFor(item),
            iconHtml: this.apiIconHtml(item),
            iconImageUrl: this.apiIconImageUrl(item),
          })),
        } satisfies CategoryGroup;
      });
  }

  private toFeaturedCategories(categories: FrontendCouponCategory[]): FeaturedCategory[] {
    return (categories as ApiCouponCategory[])
      .filter((category) => category.isFeatured === true)
      .map((category) => ({
        icon: this.categoryIconFor(category),
        iconHtml: this.apiIconHtml(category),
        iconImageUrl: this.apiIconImageUrl(category),
        slug: this.categorySlug(category),
        coupons: this.countFromUnknown(category.totalCoupons ?? category.coupons),
        stores: this.countFromUnknown(category.totalStores ?? category['stores']),
        title: category.name,
      }));
  }

  private categoryIconFor(category: ApiCouponCategory): CategoryIcon {
    const haystack = this.categorySearchText(category);

    if (/(clothing|fashion|apparel|shoes|bags|women|watch|jewelry)/.test(haystack)) {
      return 'apparel';
    }

    if (/(electronics|mobile|computer|software|internet|vpn|hosting|photography)/.test(haystack)) {
      return 'electronics';
    }

    if (/(ai|artificial intelligence)/.test(haystack)) {
      return 'ai';
    }

    if (/(art|entertainment|music|movie|film|theater|theatre|game|gaming)/.test(haystack)) {
      return 'entertainment';
    }

    return 'business';
  }

  private subcategoryIconFor(category: ApiCouponCategory): SubcategoryIcon {
    const haystack = this.categorySearchText(category);

    if (/(clothing|fashion|apparel|shoes|bags|women|watch|jewelry)/.test(haystack)) {
      return 'shirt';
    }

    if (/(electronics|mobile|computer|software|internet|vpn|hosting|photography)/.test(haystack)) {
      return 'device';
    }

    if (
      /(business|finance|office|service|education|transportation|tools|hardware)/.test(haystack)
    ) {
      return 'briefcase';
    }

    return 'spark';
  }

  private categorySearchText(category: ApiCouponCategory): string {
    return `${category.name} ${category.iconCode ?? ''}`.toLowerCase();
  }

  private categorySlug(category: ApiCouponCategory): string {
    return category.slug || this.slugify(category.name);
  }

  private apiIconHtml(category: ApiCouponCategory): SafeHtml | null {
    const iconCode = category.iconCode?.trim();

    if (category.iconType !== 'code' || !iconCode) {
      return null;
    }

    if (!/<svg[\s>]/i.test(iconCode)) {
      return null;
    }

    return this.sanitizer.bypassSecurityTrustHtml(iconCode);
  }

  private apiIconImageUrl(category: ApiCouponCategory): string {
    if (category.iconType === 'code' || !category.iconImagePath) {
      return '';
    }

    return this.assetUrl(category.iconImagePath);
  }

  private assetUrl(path: string): string {
    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const assetBaseUrl = this.apiConfig.assetBaseUrl.replace(/\/$/, '');
    const assetPath = path.startsWith('/') ? path : `/${path}`;

    return `${assetBaseUrl}${assetPath}`;
  }

  private countFromUnknown(value: unknown): number {
    if (Array.isArray(value)) {
      return value.length;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    return 0;
  }

  private slugify(value: string): string {
    return value
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
}
